'use client';
import { IHubSpotForm } from '@/sanity/globals/HubspotForm';
import { GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PortableText } from 'next-sanity';
import { PortableTextBlock } from 'sanity';
import Countdown from '../global/Countdown';
import HubSpotForm from '@/components/global/partials/HubSpotForm';
import { GroqImage } from '@/helpers/Image';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import WaitlistForm from '../global/WaitlistForm';
import FormSuccessStep from '../global/FormSuccessStep';

interface IWaitinglistFormProps {
  title: PortableTextBlock[];
  image: GROQImage;
  form: IHubSpotForm;
  enableCountdown?: boolean;
  countdownTime: string;
}

export default function WaitinglistForm({
  title,
  image,
  form,
  countdownTime,
  enableCountdown,
}: IWaitinglistFormProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [step, setStep] = useState<'form' | 'success'>('form');
  const isMobile = useIsMobile();

  // Track scroll progress of the section
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // Transform scroll progress to translateY
  // Start parallax effect only after 50% scroll (0.5)
  // Map from 0-0.5 scroll progress to 0 translateY (no movement)
  // Then from 0.5-1 scroll progress to 0-500px translateY
  const sectionY = useTransform(scrollYProgress, [0, 0.5, 1], [0, 0, 500]);

  const handleFormSuccess = () => {
    setStep('success');
  };

  return (
    <motion.section
      ref={sectionRef}
      style={{ y: isMobile ? 0 : sectionY }}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className="flex flex-wrap lg:gap-0 gap-[6rem] items-stretch lg:pb-0 pb-[10rem] lg:pt-0 pt-[13rem]"
    >
      <div className="lg:w-1/2 w-full h-[57.5rem] lg:order-start order-last lg:h-[137.5rem] overflow-clip">
        {image && (
          <GroqImage
            className="!w-full !object-cover !h-full"
            src={image}
            alt={image?.name}
          />
        )}
      </div>
      <div className="flex lg:w-1/2 flex-col gap-[6rem] items-start justify-center px-[2rem] lg:px-[19rem]">
        {step === 'form' ? (
          <motion.div
            key="form-step"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col gap-[6rem] w-full"
          >
            <div className="flex flex-col gap-[3.62rem] items-start text-offwhite uppercase prose-h2:text-[4rem] prose-h2:lg:w-full prose-h2:w-[95%] prose-h2:lg:text-[7rem]">
              {enableCountdown && (
                <div className="">
                  <Countdown date={countdownTime} enableDays enableStrings />
                </div>
              )}
              {title && (
                <PortableText value={title} components={richTextComponent} />
              )}
            </div>

            <WaitlistForm onSuccess={handleFormSuccess} />
          </motion.div>
        ) : (
          <FormSuccessStep />
        )}
      </div>
    </motion.section>
  );
}
